import React, { useState, useEffect } from "react";
import { motion } from "framer-motion";
import { Link, useNavigate, useLocation } from "react-router-dom";
import logoImg from "../static/img/Logo 250x250.png";

const Navbar = () => {
  const [isScrolled, setIsScrolled] = useState(false);
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const navigate = useNavigate();

  useEffect(() => {
    const onScroll = () => setIsScrolled(window.scrollY > 10);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  // Close mobile menu when clicking outside
  useEffect(() => {
    if (isMobileMenuOpen) {
      document.body.style.overflow = "hidden";
    } else {
      document.body.style.overflow = "unset";
    }
    return () => {
      document.body.style.overflow = "unset";
    };
  }, [isMobileMenuOpen]);

  // ⭐ NEW LOGIC → Works both on home and other pages
  const goToSection = (id) => {
    setIsMobileMenuOpen(false); // Close menu when navigating
    if (window.location.pathname !== "/") {
      // Redirect to home and tell it which section to scroll to
      navigate("/", { state: { scrollTo: id } });
    } else {
      // Already on homepage → scroll immediately
      const el = document.getElementById(id);
      if (el) {
        el.scrollIntoView({ behavior: "smooth", block: "start" });
      }
    }
  };

  const handleNavigate = (path) => {
    setIsMobileMenuOpen(false);
    navigate(path);
  };

  return (
    <motion.nav
      initial={{ y: -80 }}
      animate={{ y: 0 }}
      transition={{ duration: 0.5, ease: "easeOut" }}
      className={`fixed top-0 left-0 w-full z-50 bg-white ${
        isScrolled ? "shadow-[0_1px_2px_rgba(0,0,0,0.05)]" : ""
      } border-b border-[rgba(0,0,0,0.06)]`}
    >
      <div className="max-w-[1350px] mx-auto px-4 md:px-6 py-2 flex items-center justify-between">

        {/* ---------- LOGO ---------- */}
        <Link
          to="/"
          className="flex items-center gap-3 no-underline"
          onClick={(e) => {
            // If already on home, prevent navigation and smooth scroll to top
            if (window.location.pathname === "/") {
              e.preventDefault();
              setIsMobileMenuOpen(false);
              window.scrollTo({ top: 0, behavior: "smooth" });
            } else {
              // close mobile menu then allow Link to navigate
              setIsMobileMenuOpen(false);
            }
          }}
        >
            <img src={logoImg} alt="Bloom Social" className=" w-[110px] h-[60px] object-contain" />
        
        </Link>

        {/* ---------- MOBILE MENU TOGGLE ---------- */}
        <button
          onClick={() => setIsMobileMenuOpen(!isMobileMenuOpen)}
          className="md:hidden flex items-center justify-center w-10 h-10 rounded-lg hover:bg-gray-100 transition"
          aria-label="Toggle menu"
        >
          <div className="w-6 h-5 flex flex-col justify-between">
            <motion.span
              animate={isMobileMenuOpen ? { rotate: 45, y: 8 } : { rotate: 0, y: 0 }}
              className="w-full h-0.5 bg-[#171717] rounded-full origin-center"
            />
            <motion.span
              animate={isMobileMenuOpen ? { opacity: 0 } : { opacity: 1 }}
              className="w-full h-0.5 bg-[#171717] rounded-full"
            />
            <motion.span
              animate={isMobileMenuOpen ? { rotate: -45, y: -8 } : { rotate: 0, y: 0 }}
              className="w-full h-0.5 bg-[#171717] rounded-full origin-center"
            />
          </div>
        </button>

        {/* ---------- NAV LINKS ---------- */}
        <div className="hidden md:flex items-center gap-6 font-medium text-[16px]">

          <button
            className="text-[#171717] hover:text-[#3C5DF7] transition"
            onClick={() => goToSection("services")}
          >
            Services
          </button>

          <button
            className="text-[#171717] hover:text-[#3C5DF7] transition"
            onClick={() => goToSection("process")}
          >
            Process
          </button>

          {/* WORKS PAGE */}

          <button
            className="text-[#171717] hover:text-[#3C5DF7] transition"
            onClick={() => goToSection("faq")}
          >
            FAQ
          </button>

          <button
            className="text-[#171717] hover:text-[#3C5DF7] transition"
            onClick={() => goToSection("reviews")}
          >
            Reviews
          </button>
          <button
            className="text-[#171717] hover:text-[#3C5DF7] transition"
            onClick={() => handleNavigate("/career")}
          >
            Career
          </button>
        </div>

        {/* ---------- CONTACT BUTTON ---------- */}
        <motion.a
          href="/contact"
          whileHover={{ scale: 1.04 }}
          whileTap={{ scale: 0.97 }}
          className="hidden md:flex items-center gap-2 bg-[#171717] text-white px-6 py-3 rounded-full font-medium text-[15px]
            shadow-[0px_2px_6px_rgba(0,0,0,0.12),0px_8px_20px_rgba(0,0,0,0.06)] cursor-pointer"
        >
          Contact
          <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
            <svg width="14" height="14" viewBox="0 0 256 256" fill="#171717">
              <path d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z" />
            </svg>
          </div>
        </motion.a>

      </div>

      {/* ---------- MOBILE SIDEBAR OVERLAY ---------- */}
      {isMobileMenuOpen && (
        <motion.div
          initial={{ opacity: 0 }}
          animate={{ opacity: 1 }}
          exit={{ opacity: 0 }}
          onClick={() => setIsMobileMenuOpen(false)}
          className="md:hidden fixed inset-0 bg-black/40 backdrop-blur-sm z-40"
        />
      )}

      {/* ---------- MOBILE SIDEBAR MENU ---------- */}
      <motion.div
        initial={{ x: "100%" }}
        animate={{ x: isMobileMenuOpen ? 0 : "100%" }}
        transition={{ type: "spring", damping: 25, stiffness: 200 }}
        className="md:hidden fixed top-0 right-0 bottom-0 w-[280px] bg-white shadow-2xl z-50 flex flex-col"
      >
        {/* Close Button */}
        <div className="flex items-center justify-between p-6 border-b border-gray-100">
          <span className="text-lg font-semibold text-[#171717]">Menu</span>
          <button
            onClick={() => setIsMobileMenuOpen(false)}
            className="w-8 h-8 flex items-center justify-center rounded-lg hover:bg-gray-100 transition"
          >
            <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2">
              <path d="M18 6L6 18M6 6l12 12" />
            </svg>
          </button>
        </div>

        {/* Menu Links */}
        <nav className="flex-1 px-6 py-8 flex flex-col gap-2">
          <button
            onClick={() => goToSection("services")}
            className="text-left text-[#171717] hover:text-[#3C5DF7] hover:bg-gray-50 px-4 py-3 rounded-lg transition font-medium"
          >
            Services
          </button>

          <button
            onClick={() => goToSection("works")}
            className="text-left text-[#171717] hover:text-[#3C5DF7] hover:bg-gray-50 px-4 py-3 rounded-lg transition font-medium"
          >
            Works
          </button>

          <button
            onClick={() => goToSection("faq")}
            className="text-left text-[#171717] hover:text-[#3C5DF7] hover:bg-gray-50 px-4 py-3 rounded-lg transition font-medium"
          >
            FAQ
          </button>

          <button
            onClick={() => goToSection("reviews")}
            className="text-left text-[#171717] hover:text-[#3C5DF7] hover:bg-gray-50 px-4 py-3 rounded-lg transition font-medium"
          >
            Reviews
          </button>

          <button
            onClick={() => handleNavigate("/career")}
            className="text-left text-[#171717] hover:text-[#3C5DF7] hover:bg-gray-50 px-4 py-3 rounded-lg transition font-medium"
          >
            Career
          </button>
        </nav>

        {/* Contact Button in Mobile Menu */}
        <div className="p-6 border-t border-gray-100">
          <motion.button
            whileTap={{ scale: 0.97 }}
            onClick={() => handleNavigate("/contact")}
            className="w-full flex items-center justify-center gap-2 bg-[#171717] text-white px-6 py-4 rounded-full font-medium text-[15px]
              shadow-[0px_2px_6px_rgba(0,0,0,0.12),0px_8px_20px_rgba(0,0,0,0.06)]"
          >
            Contact
            <div className="w-8 h-8 bg-white rounded-full flex items-center justify-center">
              <svg width="14" height="14" viewBox="0 0 256 256" fill="#171717">
                <path d="M221.66,133.66l-72,72a8,8,0,0,1-11.32-11.32L196.69,136H40a8,8,0,0,1,0-16H196.69L138.34,61.66a8,8,0,0,1,11.32-11.32l72,72A8,8,0,0,1,221.66,133.66Z" />
              </svg>
            </div>
          </motion.button>
        </div>
      </motion.div>
    </motion.nav>
  );
};

export default Navbar;
